// src/components/VideoGallery.js
import React, { useState, useEffect, useRef } from 'react';
import { collection, getDocs, query, orderBy } from 'firebase/firestore';
import { db } from '../firebase';
import { Play, X, Video, ChevronLeft, ChevronRight } from 'lucide-react';

const VideoGallery = () => {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeVideo, setActiveVideo] = useState(null);
  const scrollRef = useRef(null);

  useEffect(() => {
    const fetchVideos = async () => {
      try {
        const q = query(collection(db, 'galleryVideos'), orderBy('order', 'asc'));
        const snap = await getDocs(q);
        const data = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        setVideos(data.filter(v => v.active !== false));
      } catch (error) {
        console.error('Error fetching videos:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchVideos();
  }, []);

  const scroll = (direction) => {
    if (!scrollRef.current) return;
    const amount = 320;
    scrollRef.current.scrollBy({
      left: direction === 'left' ? -amount : amount,
      behavior: 'smooth',
    });
  };

  const getEmbedUrl = (url) => {
    if (!url) return '';
    // YouTube
    const ytMatch = url.match(/(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/);
    if (ytMatch) return `https://www.youtube.com/embed/${ytMatch[1]}?autoplay=1`;
    // Vimeo
    const vimeoMatch = url.match(/vimeo\.com\/(\d+)/);
    if (vimeoMatch) return `https://player.vimeo.com/video/${vimeoMatch[1]}?autoplay=1`;
    // Direct video file
    return url;
  };

  const isDirectVideo = (url) => {
    if (!url) return false;
    return /\.(mp4|webm|ogg|mov)$/i.test(url);
  };

  if (loading) {
    return (
      <section className="max-w-7xl mx-auto px-4 md:px-6 py-10">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 animate-pulse">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="aspect-video bg-gray-200 rounded-2xl"></div>
          ))}
        </div>
      </section>
    );
  }

  if (videos.length === 0) return null;

  return (
    <>
      <section className="max-w-7xl mx-auto px-4 md:px-6 py-8 md:py-12 anim-fadeInUp">

        {/* Header */}
        <div className="text-center mb-6 md:mb-10">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full mb-3"
            style={{ backgroundColor: 'rgba(22, 163, 74, 0.1)', color: 'var(--color-link)' }}>
            <Video size={14} />
            <span className="text-xs font-bold uppercase tracking-wider">Videos</span>
          </div>
          <h2 className="text-2xl md:text-4xl font-bold mb-2" style={{ color: 'var(--color-primary-text)' }}>
            Video Gallery
          </h2>
          <p className="text-sm md:text-base" style={{ color: 'var(--color-secondary-text)' }}>
            Watch our organic journey
          </p>
        </div>

        {/* Slider */}
        <div className="relative">

          {/* Left Arrow */}
          {videos.length > 1 && (
            <button
              onClick={() => scroll('left')}
              className="hidden md:flex absolute -left-4 top-1/2 -translate-y-1/2 z-10 w-10 h-10 items-center justify-center rounded-full shadow-lg btn-2d"
              style={{ backgroundColor: 'var(--color-card-bg)', border: `1px solid var(--color-border)`, color: 'var(--color-primary-text)' }}
            >
              <ChevronLeft size={20} />
            </button>
          )}

          {/* Scroll Container */}
          <div
            ref={scrollRef}
            className="flex gap-4 overflow-x-auto scroll-smooth pb-4 snap-x snap-mandatory"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
          >
            {videos.map((video, i) => (
              <div
                key={video.id}
                className="relative shrink-0 w-[280px] md:w-[340px] aspect-video rounded-2xl overflow-hidden shadow-lg group cursor-pointer snap-start anim-scaleIn"
                style={{ animationDelay: `${i * 100}ms`, border: `1px solid var(--color-border)` }}
                onClick={() => setActiveVideo(video)}
              >
                {/* Thumbnail */}
                <img
                  src={video.thumbnail}
                  alt={video.title || 'Video'}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                />

                {/* Dark Overlay */}
                <div className="absolute inset-0 bg-black/40 group-hover:bg-black/60 transition-all duration-300"></div>

                {/* Play Button */}
                <div className="absolute inset-0 flex items-center justify-center">
                  <div
                    className="w-14 h-14 md:w-16 md:h-16 rounded-full flex items-center justify-center shadow-2xl group-hover:scale-110 transition-transform duration-300 anim-pulseGlow"
                    style={{ backgroundColor: 'rgba(255,255,255,0.95)' }}
                  >
                    <Play size={24} className="md:w-7 md:h-7 ml-1" style={{ color: '#16a34a' }} fill="#16a34a" />
                  </div>
                </div>

                {/* Title */}
                {video.title && (
                  <div className="absolute bottom-0 left-0 right-0 p-3 bg-gradient-to-t from-black/90 to-transparent">
                    <p className="text-white text-sm font-semibold line-clamp-2">{video.title}</p>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Right Arrow */}
          {videos.length > 1 && (
            <button
              onClick={() => scroll('right')}
              className="hidden md:flex absolute -right-4 top-1/2 -translate-y-1/2 z-10 w-10 h-10 items-center justify-center rounded-full shadow-lg btn-2d"
              style={{ backgroundColor: 'var(--color-card-bg)', border: `1px solid var(--color-border)`, color: 'var(--color-primary-text)' }}
            >
              <ChevronRight size={20} />
            </button>
          )}
        </div>

        {/* Scroll hint */}
        {videos.length > 2 && (
          <p className="text-center text-xs mt-3 md:hidden" style={{ color: 'var(--color-secondary-text)' }}>
            ← Swipe to see more →
          </p>
        )}
      </section>

      {/* Fullscreen Video Modal */}
      {activeVideo && (
        <div
          className="fixed inset-0 z-[100] bg-black bg-opacity-95 flex items-center justify-center p-4 anim-fadeIn"
          onClick={() => setActiveVideo(null)}
        >
          <button
            onClick={() => setActiveVideo(null)}
            className="absolute top-4 right-4 md:top-6 md:right-6 p-2 md:p-3 rounded-full bg-white/10 hover:bg-white/20 text-white backdrop-blur-md transition z-10"
          >
            <X size={24} />
          </button>

          <div
            className="w-full max-w-5xl anim-scaleIn"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="aspect-video bg-black rounded-2xl overflow-hidden shadow-2xl">
              {isDirectVideo(activeVideo.videoUrl) ? (
                <video
                  src={activeVideo.videoUrl}
                  controls
                  autoPlay
                  className="w-full h-full"
                />
              ) : (
                <iframe
                  src={getEmbedUrl(activeVideo.videoUrl)}
                  title={activeVideo.title || 'Video'}
                  className="w-full h-full"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              )}
            </div>

            {activeVideo.title && (
              <p className="text-white text-center mt-4 text-base md:text-lg font-semibold">
                {activeVideo.title}
              </p>
            )}
            {activeVideo.description && (
              <p className="text-white/70 text-center mt-1 text-sm">
                {activeVideo.description}
              </p>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default VideoGallery;